import React, { useCallback, useEffect, useRef } from 'react';
import { scaleBand, scaleLinear, max, axisLeft, select, axisBottom } from 'd3';
import * as d3 from 'd3';
import styles from '../TestStackedD/StackedBarChart.module.css';
import { useDispatch, useSelector } from "react-redux";
import HeaderDiagram from "../HeaderD/HeaderDiagram";
import icons from "../../../common/icons/icons";
import style from "../TesTreeD/TreeMapDiagramm.module.css";
import Legend from "../../../components/DiagrammLegend/Legend";
import useResizeObserver from 'use-resize-observer';
import Spinner from "../../TestPages/Spinner";
import { formatNumberWithDecimal } from "../../../utils/countFunc";
import {
    addStackedShortName,
    fetchIspOkpdData,
    setSelectedProductStackedShort
} from "../../../service/reducers/StackedLineShort";
import { formatCurrency } from "../../../utils/rubbleFunc";
import { cancelAllPendingRequests } from "../../../api/api";
import tooltipNames from "../../../utils/tooltipTitles.json";
import { setActiveLegendItem } from "../../../service/reducers/legendItemsClick";
import useFetchData from "../../../hook/useFetchData";
import useTooltip from '../../../hook/useTooltip';
import Tooltip from '../../../components/Tooltip/Tooltip';

const IspStackedBarChart = ({ onZoomClick, zoomedDiagram }) => {
    const svgRefTest = useRef();
    const dispatch = useDispatch();
    const { tooltip, tooltipRef, showTooltip, hideTooltip } = useTooltip();
    const { ref, width, height } = useResizeObserver();
    const selectedCountryLine = useSelector((state) => state.ispOkpd.selectedOkpd);
    const { odkpData, loading } = useSelector((state) => state.ispOkpd);
    const activeTab = useSelector((state) => state.tabs.activeTab);
    const isLoadingMenu = useSelector(state => state.menu.isLoadingMenu);

    useFetchData(fetchIspOkpdData, [
        useSelector(state => state.bubbleSegmentSlice.bubbleSelectedSegments),
        useSelector(state => state.segmentNameSlice.currentSegmentName),
        useSelector(state => state.donutKbrSegmentSlice.selectedKbrSegments),
        useSelector(state => state.okpdComboSelect.okpdComboData),
        useSelector(state => state.dateSlice.selectedDate),
        useSelector(state => state.pie.selectedSlice),
        useSelector(state => state.region.activeRegions),
        useSelector(state => state.activitySlice),
        useSelector(state => state.treeMapSlice.selectedSegments),
        useSelector(state => state.barLineChartMonth.selectedMonth),
        useSelector(state => state.organization.relatedINNs),
        useSelector(state => state.contractOkpd.selectedOkpd),
        useSelector(state => state.contractOkpd.trimCode),
        useSelector(state => state.donutRolesSlice.selectedSegments),
        useSelector(state => state.contractMonth1Slice.selectedContractMonth),
        useSelector(state => state.organization.searchOrgINNINNs),
        useSelector(state => state.organization.searchSuppINNINNINNs),
        useSelector(state => state.searchSwitcher.position)
    ]);

    useEffect(() => {
        if (loading === 'successful' && ((width && height))) {
            drawChart(odkpData.nodes);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [width, height, odkpData, selectedCountryLine, isLoadingMenu]);

    const headerWithThreeButtons = {
        title: "Страны происхождения продукции",
        icons: [
            { name: 'zoom', icon: zoomedDiagram === undefined ? icons.zoom : icons.zoomOut, width: 20, height: 20, onClick: onZoomClick },
            { name: 'menu', icon: icons.menu, width: 20, height: 20 }
        ]
    };

    const handleClick = useCallback((productId) => {
        cancelAllPendingRequests()
        dispatch(setSelectedProductStackedShort(productId));
        dispatch(addStackedShortName('Страны происхождения продукции'));
        dispatch(setActiveLegendItem({ diagramId: 'Страны происхождения продукции', activeItem: productId }));
    }, [dispatch]);

    const onLegendItemClick = (productId) => {
        cancelAllPendingRequests()
        onMouseOut();
        dispatch(setSelectedProductStackedShort(productId));
        dispatch(addStackedShortName('Страны происхождения продукции'));
    };

    const isActiveBar = (label) => {
        if (selectedCountryLine.length === 0) return true;
        return selectedCountryLine.includes(label);
    };

    const onMouseMove = (event, label, value) => {
        const tooltipConfig = tooltipNames.StackCountryLines.Tabs[activeTab];
        const textData = [
            { label: tooltipConfig.label, value: label },
            { label: tooltipConfig.value, value: formatCurrency(value) }
        ];
        showTooltip(event, textData, true);
    };

    const onMouseOut = () => {
        hideTooltip();
    };

    const onYLabelHover = (event, fullText) => {
        const textData = [{ value: fullText }];
        showTooltip(event, textData, false);
    };

    const drawChart = (data) => {
        const barHeight = 20;
        const margin = { top: 10, right: 30, bottom: 0, left: 55 };
        const container1 = document.getElementById("container1");
        if (!container1) return;
        const radius = 5;
        const minimumBarWidth = 10;
        const containerWidth = parseFloat(window.getComputedStyle(container1).width);
        const width = containerWidth - margin.left - margin.right;
        const height = barHeight * data.length;
        const minWidthPerTick = 50;
        const numberOfTicks = Math.floor(width / minWidthPerTick / 2);
        const svg = select(svgRefTest.current);
        svg.selectAll("*").remove();
        svg.attr("width", width + margin.left + margin.right)
            .attr("height", height + margin.top + margin.bottom);
        const g = svg.append("g")
            .attr("transform", `translate(${margin.left}, ${margin.top})`);

        const x = scaleLinear()
            .domain([0, max(data, (d) => d.value)])
            .range([0, width]);

        const y = scaleBand().domain(data.map((d) => d.label))
            .range([0, height])
            .padding(0.1);

        g.append("g")
            .call(axisLeft(y))
            .select(".domain").attr("stroke", "#EDF1F5");
        g.selectAll(".tick text")
            .attr("fill", "#8D96B2")
            .attr("x", -25)
            .attr("dy", "0.5em")
            .attr("font-family", "Golos Regular")
            .attr("cursor", "pointer")
            .attr("text-anchor", "middle")

        function truncateText(text, maxLength) {
            return text.length > maxLength ? text.slice(0, maxLength - 3) + "..." : text;
        }

        const selectedCountriesMap = new Set(selectedCountryLine);
        g.selectAll(".tick text")
            .attr("fill", (d) => selectedCountriesMap.has(d) ? "black" : "#8D96B2")
            .attr("data-title", d => d)
            .attr("cursor", "pointer")
            .text(d => truncateText(d, 7))
            .on('click', function (event, d) {
                event.stopPropagation();
                onMouseOut();
                handleClick(d)
                select(this).attr("fill", selectedCountriesMap.has(d) ? "#8D96B2" : "black");
            })
            .on('mouseover', function (event, d) {
                onYLabelHover(event, d);
            })
            .on('mouseout', onMouseOut);

        const xAxisSvg1 = select("#xAxisSvg1");
        xAxisSvg1.selectAll("*").remove();
        xAxisSvg1.attr("width", width + margin.left + margin.right)
            .attr("height", 30);
        xAxisSvg1.append("g")
            .attr("transform", `translate(${margin.left}, 0)`)
            .call(axisBottom(x).ticks(numberOfTicks).tickFormat((d) => formatNumberWithDecimal(d)))
            .selectAll("g.tick")
            .attr("font-family", "Golos Regular")
            .each(function () {
                select(this).select("text")
                    .attr("fill", "#8D96B2")
                    .attr("font-family", "Golos Regular");
            });
        xAxisSvg1.select(".domain").attr("stroke", "#EDF1F5");
        xAxisSvg1.selectAll(".tick line").remove();

        //убрал все тики с свг?
        g.selectAll(".tick line").remove();

        data.forEach((barData) => {
            const isActive = isActiveBar(barData.label);
            const barWidth = Math.max(x(barData.value), minimumBarWidth);
            const barY = y(barData.label);
            const pathData = `M0,${barY} h${barWidth - radius} a${radius},${radius} 0 0 1 ${radius},${radius} v${y.bandwidth() - 2 * radius} a${radius},${radius} 0 0 1 ${-radius},${radius} h${-(barWidth - radius)} Z`;

            const path = g.append("path")
                .attr("d", pathData)
                .attr("x", 0)
                .attr("y", y(barData.label))
                .attr("width", 0)
                .attr("height", y.bandwidth())
                .attr("fill", "#4B72FB")
                .attr("opacity", isActive ? 1 : 0.3);

            path.transition()
                .duration(800)
                .attrTween("d", function () {
                    const i = d3.interpolate(0, barWidth);
                    return function (t) {
                        const interpolatedWidth = i(t);
                        return `M0,${barY} h${interpolatedWidth - radius} a${radius},${radius} 0 0 1 ${radius},${radius} v${y.bandwidth() - 2 * radius} a${radius},${radius} 0 0 1 ${-radius},${radius} h${-(interpolatedWidth - radius)} Z`;
                    };
                })
                .on('end', () => {
                    if (selectedCountryLine.length > 0 && selectedCountryLine.includes(barData.label)) {
                        const textElement = g.append("text")
                            .attr("x", x(barData.value) + 5)
                            .attr("y", y(barData.label) + y.bandwidth() / 2)
                            .attr("dy", ".35em")
                            .attr("font-family", "Golos Regular")
                            .attr("font-size", "12px")
                            .attr("fill", "var(--text-color, #FFF)")
                            .attr("opacity", 0)
                            .text(formatNumberWithDecimal(barData.value));
                        textElement.on('mousemove', (event) => onMouseMove(event, barData.label, barData.value))
                            .on('mouseout', onMouseOut);

                        const textWidth = textElement.node().getComputedTextLength();
                        const remainingSpaceRight = width - (x(barData.value) + 5);

                        if (textWidth > remainingSpaceRight) {
                            textElement.attr("x", x(barData.value) - textWidth - 5)
                                .attr("fill", "#EDF1F5");
                        }

                        textElement.transition()
                            .delay(0.4)
                            .attr("opacity", 1);
                    }

                    path.on('mousemove', (event) => onMouseMove(event, barData.label, barData.value))
                        .on('mouseout', onMouseOut)
                        .on('click', (event) => {
                            event.stopPropagation();
                            onMouseOut();
                            handleClick(barData.label);
                        });
                });
        });

    };

    return (
        <div ref={ref} className={`${style.container} ${selectedCountryLine.length > 0 ? style.selected : ''}  ${zoomedDiagram ? style.zoomed : ''} my-svg-diagram`}>
            {isLoadingMenu || loading === 'pending' || loading === 'failed' ? (
                <Spinner />
            ) : (
                <>
                    <div className={style.header}>
                        <HeaderDiagram
                            {...headerWithThreeButtons}
                            onZoomClick={onZoomClick}
                            diagramName={headerWithThreeButtons.title}
                        />
                        <Legend diagramId={"Страны происхождения продукции"} data={odkpData.nodes} dynamicRadius={zoomedDiagram ? 150 : 75} activeColors={['#4B72FB']} onLegendItemClick={onLegendItemClick} selectedSlice={selectedCountryLine} />
                    </div>
                    <Tooltip x={tooltip.x} y={tooltip.y} text={tooltip.text} showColon={tooltip.showColon} ref={tooltipRef} />
                    <div className={`${styles.containerContent} ${zoomedDiagram ? styles.zoomed : ''}`} id="container1">
                        <svg ref={svgRefTest}></svg>
                    </div>
                    <div id="xAxisContainer1">
                        <svg id="xAxisSvg1"></svg>
                    </div>
                </>
            )}
        </div>
    );

};

export default IspStackedBarChart;
